import { taskPriorities, taskPriorityLabels, type TaskPriority } from '@nexo/types';
import {
  Avatar,
  Button,
  EmptyState,
  SegmentedControl,
  Select,
  cn,
  type SelectOption,
} from '@nexo/ui';
import { CheckCircle2, Columns3, Inbox, List, Plus, Search } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router';
import { TeamHeader } from '../../layouts/TeamHeader';
import { useTeam } from '../../layouts/TeamContext';
import { useSprints, useTasks } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { TaskBoard } from './TaskBoard';
import { TaskGroupList } from './TaskGroupList';
import { PriorityIcon } from './TaskIcons';
import {
  dueBucket,
  filterTasks,
  groupByDue,
  groupByLabels,
  groupTasks,
  type GroupBy,
} from './taskUtils';
import { useCreateTask } from './useTaskMutations';

type View = 'mine' | 'all' | 'board';
const views: View[] = ['mine', 'all', 'board'];
const GROUP_KEY = 'nexo.tasks.groupBy';

export function TasksPage() {
  const { t } = useTranslation();
  const { team, members, channels, openTask } = useTeam();
  const me = useMe();
  const tasks = useTasks(team.id);
  const activeSprint = useSprints(team.id).data?.find((s) => s.status === 'active');
  const [params, setParams] = useSearchParams();
  const view: View = views.includes(params.get('view') as View)
    ? (params.get('view') as View)
    : 'mine';

  const [query, setQuery] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState<TaskPriority | ''>('');
  const [showDone, setShowDone] = useState(false);
  // Like Jira, the board shows the active sprint (when there is one).
  const [boardScope, setBoardScope] = useState<'sprint' | 'all'>('sprint');
  const boardSprint = boardScope === 'sprint' ? activeSprint : undefined;
  const [groupBy, setGroupBy] = useState<GroupBy>(
    () => (localStorage.getItem(GROUP_KEY) as GroupBy | null) ?? 'status',
  );

  const all = tasks.data ?? [];
  const mine = all.filter((task) => task.assignees.some((a) => a.id === me.id));
  const openMine = mine.filter((task) => task.status !== 'done');

  const source =
    view === 'mine'
      ? mine
      : view === 'board' && boardSprint
        ? all.filter((task) => task.sprintId === boardSprint.id)
        : all;
  const scoped = filterTasks(source, {
    query,
    taskKey: team.taskKey,
    assigneeId: view === 'mine' ? null : assigneeId || null,
    priority: priority || null,
    // The board always shows the "done" column; lists hide completed unless asked.
    includeDone: view === 'board' ? true : showDone,
  });

  const setView = (next: View) =>
    setParams((p) => {
      p.set('view', next);
      return p;
    });

  const counts = {
    overdue: openMine.filter((task) => dueBucket(task) === 'overdue').length,
    today: openMine.filter((task) => dueBucket(task) === 'today').length,
    open: openMine.length,
  };

  // The backlog moved to the Sprints module.
  if (params.get('view') === 'backlog') {
    return <Navigate to={`/app/t/${team.id}/sprints?tab=plan`} replace />;
  }

  return (
    <>
      <TeamHeader
        actions={
          <Button
            size="sm"
            icon={<Plus className="size-4" />}
            onClick={() =>
              openTask({
                mode: 'create',
                assigneeIds: view === 'mine' ? [me.id] : [],
                sprintId: view === 'board' ? (boardSprint?.id ?? null) : null,
              })
            }
          >
            <span className="max-sm:hidden">{t('Nueva tarea')}</span>
          </Button>
        }
      >
        <h1 className="truncate text-[15px] font-semibold">{t('Tareas')}</h1>
        <SegmentedControl<View>
          label={t('Vista')}
          value={view}
          onChange={setView}
          className="ml-2 max-md:hidden"
          options={[
            { value: 'mine', label: t('Mis tareas'), icon: <Inbox />, count: counts.open },
            { value: 'all', label: t('Todas'), icon: <List /> },
            { value: 'board', label: t('Tablero'), icon: <Columns3 /> },
          ]}
        />
      </TeamHeader>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5 sm:px-5">
        <SegmentedControl<View>
          label={t('Vista')}
          size="sm"
          value={view}
          onChange={setView}
          className="md:hidden"
          options={[
            { value: 'mine', label: t('Mías') },
            { value: 'all', label: t('Todas') },
            { value: 'board', label: t('Tablero') },
          ]}
        />
        <label className="relative w-full sm:w-56">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-subtle" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Filtrar…')}
            aria-label={t('Filtrar tareas')}
            className="h-8 w-full rounded-lg bg-surface-2 pr-2 pl-8 text-sm ring-1 ring-inset ring-border placeholder:text-subtle focus:ring-accent focus:outline-none"
          />
        </label>
        {view === 'board' && activeSprint && (
          <ToolbarSelect
            label={t('Tareas del tablero')}
            value={boardScope}
            onChange={(v) => setBoardScope(v as 'sprint' | 'all')}
            options={[
              { value: 'sprint', label: activeSprint.name },
              { value: 'all', label: t('Todas las tareas') },
            ]}
          />
        )}
        {view !== 'mine' && (
          <ToolbarSelect
            label={t('Responsable')}
            value={assigneeId}
            onChange={setAssigneeId}
            options={[
              { value: '', label: t('Todas las personas') },
              { value: me.id, label: t('Asignadas a mí') },
              ...members
                .filter((m) => m.id !== me.id)
                .map((m) => ({
                  value: m.id,
                  label: m.displayName,
                  icon: <Avatar id={m.id} name={m.displayName} src={m.avatarUrl} size="xs" />,
                })),
            ]}
          />
        )}
        <ToolbarSelect
          label={t('Prioridad')}
          value={priority}
          onChange={(v) => setPriority(v as TaskPriority | '')}
          options={[
            { value: '', label: t('Cualquier prioridad') },
            ...taskPriorities.map((p) => ({
              value: p,
              label: t(taskPriorityLabels[p], { context: 'priority' }),
              icon: <PriorityIcon priority={p} showNormal />,
            })),
          ]}
        />
        {view === 'all' && (
          <ToolbarSelect
            label={t('Agrupar por')}
            value={groupBy}
            onChange={(v) => {
              setGroupBy(v as GroupBy);
              localStorage.setItem(GROUP_KEY, v);
            }}
            options={(Object.keys(groupByLabels) as GroupBy[]).map((key) => ({
              value: key,
              label: t('Agrupar: {{field}}', { field: t(groupByLabels[key]) }),
            }))}
          />
        )}
        {(view === 'mine' || view === 'all') && (
          <button
            type="button"
            aria-pressed={showDone}
            onClick={() => setShowDone((v) => !v)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] ring-1 ring-inset',
              showDone
                ? 'bg-accent-soft text-accent ring-accent/30'
                : 'text-muted ring-border hover:bg-surface-2',
            )}
          >
            <CheckCircle2 className="size-4" />
            {t('Completadas')}
          </button>
        )}
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        {view === 'board' ? (
          <TaskBoard tasks={scoped} sprintId={boardSprint?.id ?? null} />
        ) : (
          <div className="mx-auto max-w-4xl px-3 py-4 sm:px-6">
            {view === 'mine' && <MyTasksSummary {...counts} />}
            <QuickAdd assignToMe={view === 'mine'} />
            {scoped.length === 0 ? (
              <EmptyState
                icon={<Inbox className="size-8" />}
                title={view === 'mine' ? t('No tienes tareas pendientes') : t('No hay tareas')}
                description={
                  view === 'mine'
                    ? t('Cuando alguien te asigne una tarea aparecerá aquí, ordenada por urgencia.')
                    : t('Crea una tarea o convierte un mensaje del chat en tarea.')
                }
              />
            ) : (
              <TaskGroupList
                groups={
                  view === 'mine'
                    ? groupByDue(scoped)
                    : groupTasks(scoped, groupBy, { channels, members })
                }
                collapsedByDefault={['done']}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

function MyTasksSummary({
  overdue,
  today,
  open,
}: {
  overdue: number;
  today: number;
  open: number;
}) {
  const { t } = useTranslation();
  const items = [
    { label: t('Pendientes'), value: open, tone: 'text-text' },
    { label: t('Para hoy'), value: today, tone: today ? 'text-warning' : 'text-text' },
    { label: t('Vencidas'), value: overdue, tone: overdue ? 'text-danger' : 'text-text' },
  ];
  return (
    <div className="mb-5 grid grid-cols-3 gap-2 sm:gap-3">
      {items.map((item) => (
        <div key={item.label} className="rounded-xl border border-border bg-surface-2/50 px-4 py-3">
          <p className={cn('text-2xl font-semibold tracking-tight tabular-nums', item.tone)}>
            {item.value}
          </p>
          <p className="text-xs text-muted">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

function QuickAdd({ assignToMe }: { assignToMe: boolean }) {
  const { t } = useTranslation();
  const me = useMe();
  const create = useCreateTask();
  const [title, setTitle] = useState('');

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const value = title.trim();
    if (!value) return;
    create.mutate({ title: value, assigneeIds: assignToMe ? [me.id] : [] });
    setTitle('');
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mb-4 flex items-center gap-3 rounded-xl border border-dashed border-border-strong px-3.5 py-2.5 focus-within:border-accent focus-within:border-solid"
    >
      <Plus className="size-4 text-subtle" />
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={
          assignToMe
            ? t('Añadir una tarea para mí y pulsar Enter')
            : t('Añadir una tarea y pulsar Enter')
        }
        aria-label={t('Nueva tarea rápida')}
        maxLength={200}
        className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-subtle focus:outline-none"
      />
    </form>
  );
}

function ToolbarSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}) {
  return (
    <div className="w-auto max-w-56">
      <Select
        aria-label={label}
        value={value}
        onChange={onChange}
        options={options}
        variant="subtle"
        size="sm"
      />
    </div>
  );
}
